import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  Monitor, 
  Mic, 
  Settings as SettingsIcon, 
  Copy, 
  ExternalLink,
  Radio,
  Users,
  Play,
  Square,
  AlertCircle,
  UserPlus,
  Crown,
  Video,
  Grid,
  QrCode,
  Share2
} from 'lucide-react';
import { useStream } from '../context/StreamContext';
import { useAuth } from '../context/AuthContext';
import HostManager from './StreamHost/HostManager';
import MultiCameraView from './MultiCameraView';
import QRCodeShare from './QRCodeShare';

function StreamHost() {
  const { createStream, streams, isLoading } = useStream();
  const { user, canCreateStreams } = useAuth();
  const [currentStream, setCurrentStream] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRShare, setShowQRShare] = useState(false);
  const [activeTab, setActiveTab] = useState('camera'); // 'camera', 'hosts', 'broadcast', 'settings'
  const [hosts, setHosts] = useState([]);
  const [streamConfig, setStreamConfig] = useState({
    name: '',
    description: '',
    recording: true,
    allowOrigins: '*',
    requireSignedURLs: false,
    multiHost: true,
    maxHosts: 6,
  });
  const [devices, setDevices] = useState({
    cameras: [],
    microphones: [],
  });
  const [selectedDevices, setSelectedDevices] = useState({
    camera: '',
    microphone: '',
  });
  const [connectionInfo, setConnectionInfo] = useState(null);

  useEffect(() => {
    loadDevices();
    // Auto-create user as primary host when they have a stream
    if (currentStream && user) {
      initializePrimaryHost();
    }
  }, [currentStream, user]);

  const loadDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      setDevices({
        cameras: devices.filter(device => device.kind === 'videoinput'),
        microphones: devices.filter(device => device.kind === 'audioinput'),
      });
      
      // Set default devices
      setSelectedDevices({
        camera: devices.find(d => d.kind === 'videoinput')?.deviceId || '',
        microphone: devices.find(d => d.kind === 'audioinput')?.deviceId || '',
      });
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
    }
  };

  const initializePrimaryHost = () => {
    const primaryHost = {
      id: user.id,
      displayName: user.displayName,
      email: user.email,
      role: 'primary-host',
      isOnline: true,
      hasCamera: false,
      hasMicrophone: false,
      isScreenSharing: false,
      avatar: user.avatar,
      permissions: {
        camera: true,
        microphone: true,
        screenShare: true,
        chat: true,
        viewerInteraction: true,
        streamControl: true,
      },
      lastSeen: new Date(),
    };

    setHosts([primaryHost]);
  };

  const handleCreateStream = async (e) => {
    e.preventDefault();
    
    if (!canCreateStreams()) {
      alert('You do not have permission to create streams');
      return;
    }
    
    try {
      const newStream = await createStream({
        name: streamConfig.name,
        meta: {
          description: streamConfig.description,
          hostDevices: selectedDevices,
          multiHost: streamConfig.multiHost,
          maxHosts: streamConfig.maxHosts,
        },
        recording: {
          mode: streamConfig.recording ? 'automatic' : 'off',
          timeoutSeconds: 300,
        },
        allowedOrigins: streamConfig.allowOrigins === '*' ? ['*'] : [streamConfig.allowOrigins],
        requireSignedURLs: streamConfig.requireSignedURLs,
      });
      
      setCurrentStream(newStream);
      setShowCreateModal(false);
      setStreamConfig({
        name: '',
        description: '',
        recording: true,
        allowOrigins: '*',
        requireSignedURLs: false,
        multiHost: true,
        maxHosts: 6,
      });
    } catch (error) {
      console.error('Failed to create stream:', error);
    }
  };

  const handleStartStreaming = () => {
    setIsStreaming(true);
  };

  const handleStopStreaming = () => {
    setIsStreaming(false);
  };

  const handleConnectionChange = (connected, clientId) => {
    setConnectionInfo({ connected, clientId });
  };

  const handleHostsChange = (updatedHosts) => {
    setHosts(updatedHosts);
  };

  const handleHostMediaChange = (hostId, mediaState) => {
    setHosts(prev => prev.map(h => h.id === hostId ? {
      ...h,
      hasCamera: mediaState.camera,
      hasMicrophone: mediaState.microphone,
      isScreenSharing: mediaState.screenShare,
    } : h));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const openInOBS = () => {
    if (currentStream?.rtmpUrl && currentStream?.rtmpKey) {
      const obsUrl = `obs://connect?server=${encodeURIComponent(currentStream.rtmpUrl)}&key=${encodeURIComponent(currentStream.rtmpKey)}`;
      window.open(obsUrl, '_blank');
    }
  };

  const tabs = [
    { id: 'camera', label: 'Multi-Camera', icon: Video, description: 'Manage cameras and hosts' },
    { id: 'hosts', label: 'Host Management', icon: Users, description: 'Add and manage hosts' },
    { id: 'broadcast', label: 'Broadcasting', icon: Radio, description: 'External streaming setup' },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, description: 'Stream configuration' },
  ];

  const getOnlineHostsCount = () => hosts.filter(h => h.isOnline).length;
  const getHostsWithCamera = () => hosts.filter(h => h.hasCamera).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Host Stream</h1>
          <p className="text-gray-400">Multi-host streaming with camera sharing and professional broadcasting</p>
        </div>
        
        {!currentStream && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            disabled={isLoading || !canCreateStreams()}
          >
            <Radio className="w-5 h-5 mr-2" />
            Create Stream
          </button>
        )}
      </div>

      {/* Active Stream Section */}
      {currentStream ? (
        <div className="space-y-6">
          {/* Stream Status */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">{currentStream.name}</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowQRShare(true)}
                  className="bg-cf-orange hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  <QrCode className="w-4 h-4" />
                  <span>Share QR</span>
                </button>
                
                {isStreaming ? (
                  <button
                    onClick={handleStopStreaming}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Square className="w-4 h-4" />
                    <span>Stop Stream</span>
                  </button>
                ) : (
                  <button
                    onClick={handleStartStreaming}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Play className="w-4 h-4" />
                    <span>Start Stream</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Stream ID</p>
                <p className="text-white font-mono text-xs">{currentStream.id}</p>
              </div>
              
              <div>
                <p className="text-gray-400">Status</p>
                <span className={isStreaming ? 'status-live' : 'status-offline'}>
                  {isStreaming ? 'LIVE' : 'OFFLINE'}
                </span>
              </div>
              
              <div>
                <p className="text-gray-400">Hosts Online</p>
                <div className="flex items-center space-x-1">
                  <Crown className="w-4 h-4 text-yellow-400" />
                  <span className="text-white">{getOnlineHostsCount()}</span>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400">Cameras Active</p>
                <div className="flex items-center space-x-1">
                  <Camera className="w-4 h-4 text-green-400" />
                  <span className="text-white">{getHostsWithCamera()}</span>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400">Viewers</p>
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-white">{connectionInfo?.connected ? '1+' : '0'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-700">
            <nav className="flex space-x-8">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-cf-orange text-cf-orange'
                        : 'border-transparent text-gray-400 hover:text-white'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <div className="text-left">
                      <div>{tab.label}</div>
                      <div className="text-xs text-gray-500">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="card">
            {/* Multi-Camera Tab */}
            {activeTab === 'camera' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Multi-Camera View</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400 text-sm">
                      {getHostsWithCamera()} of {getOnlineHostsCount()} hosts streaming
                    </span>
                  </div>
                </div>
                
                <MultiCameraView
                  streamId={currentStream.id}
                  hosts={hosts}
                  userType="host"
                  onHostMediaChange={handleHostMediaChange}
                />
              </div>
            )}

            {/* Host Management Tab */}
            {activeTab === 'hosts' && (
              <HostManager
                streamId={currentStream.id}
                onHostsChange={handleHostsChange}
              />
            )}

            {/* Broadcasting Tab */}
            {activeTab === 'broadcast' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">External Broadcasting (OBS/XSplit)</h3>
                
                <div className="space-y-4">
                  <div className="bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-400 font-medium">Professional Broadcasting Setup</p>
                        <p className="text-gray-300 text-sm mt-1">
                          Use these settings in OBS or similar software for professional multi-camera production.
                          The multi-camera view above is for direct browser streaming.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        RTMP Server URL
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={currentStream.rtmpUrl || 'Generating...'}
                          readOnly
                          className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm font-mono"
                        />
                        <button
                          onClick={() => copyToClipboard(currentStream.rtmpUrl)}
                          className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors duration-200"
                          disabled={!currentStream.rtmpUrl}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Stream Key
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="password"
                          value={currentStream.rtmpKey || 'Generating...'}
                          readOnly
                          className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm font-mono"
                        />
                        <button
                          onClick={() => copyToClipboard(currentStream.rtmpKey)}
                          className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors duration-200"
                          disabled={!currentStream.rtmpKey}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={openInOBS}
                      className="btn-secondary flex items-center space-x-2"
                      disabled={!currentStream.rtmpUrl || !currentStream.rtmpKey}
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Open in OBS</span>
                    </button>
                    
                    <a
                      href={currentStream.playbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary flex items-center space-x-2"
                      disabled={!currentStream.playbackUrl}
                    >
                      <Play className="w-4 h-4" />
                      <span>Test Playback</span>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">Stream Settings</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Multi-Host Settings */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-white">Multi-Host Configuration</h4>
                    
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={streamConfig.multiHost}
                          onChange={(e) => setStreamConfig(prev => ({ ...prev, multiHost: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-gray-300">Enable multi-host streaming</span>
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Maximum Hosts
                      </label>
                      <select
                        value={streamConfig.maxHosts}
                        onChange={(e) => setStreamConfig(prev => ({ ...prev, maxHosts: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                        disabled={!streamConfig.multiHost}
                      >
                        <option value={2}>2 Hosts</option>
                        <option value={4}>4 Hosts</option>
                        <option value={6}>6 Hosts</option>
                        <option value={8}>8 Hosts</option>
                        <option value={12}>12 Hosts</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Device Settings */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-white">Default Device Settings</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Default Camera
                      </label>
                      <select
                        value={selectedDevices.camera}
                        onChange={(e) => setSelectedDevices(prev => ({
                          ...prev,
                          camera: e.target.value
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      >
                        <option value="">Select Camera</option>
                        {devices.cameras.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Default Microphone
                      </label>
                      <select
                        value={selectedDevices.microphone}
                        onChange={(e) => setSelectedDevices(prev => ({
                          ...prev,
                          microphone: e.target.value
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      >
                        <option value="">Select Microphone</option>
                        {devices.microphones.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* No Stream Created */
        <div className="card text-center py-12">
          <Radio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No Active Stream</h3>
          <p className="text-gray-500 mb-6">Create a new multi-host stream to start broadcasting with multiple cameras</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            disabled={!canCreateStreams()}
          >
            {canCreateStreams() ? 'Create Your First Stream' : 'Upgrade to Create Streams'}
          </button>
        </div>
      )}

      {/* Create Stream Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Create New Multi-Host Stream</h3>
            
            <form onSubmit={handleCreateStream} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Stream Name
                </label>
                <input
                  type="text"
                  value={streamConfig.name}
                  onChange={(e) => setStreamConfig(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-1 focus:ring-cf-orange focus:border-transparent"
                  placeholder="My Multi-Host Stream"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Description
                </label>
                <textarea
                  value={streamConfig.description}
                  onChange={(e) => setStreamConfig(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-1 focus:ring-cf-orange focus:border-transparent"
                  placeholder="Collaborative streaming with multiple hosts..."
                  rows="3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Maximum Hosts
                </label>
                <select
                  value={streamConfig.maxHosts}
                  onChange={(e) => setStreamConfig(prev => ({ ...prev, maxHosts: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value={2}>2 Hosts</option>
                  <option value={4}>4 Hosts</option>
                  <option value={6}>6 Hosts</option>
                  <option value={8}>8 Hosts</option>
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="recording"
                  checked={streamConfig.recording}
                  onChange={(e) => setStreamConfig(prev => ({ ...prev, recording: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="recording" className="text-sm text-gray-300">
                  Enable automatic recording
                </label>
              </div>
              
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Stream'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Share Modal */}
      {showQRShare && currentStream && (
        <QRCodeShare
          url={`${window.location.origin}/viewer/${currentStream.id}`}
          title={`Join ${currentStream.name} on CloudStream`}
          description={currentStream.meta?.description || 'Join this live stream'}
          streamId={currentStream.id}
          hostName={user?.displayName}
          onClose={() => setShowQRShare(false)}
        />
      )}
    </div>
  );
}

export default StreamHost; 